/** Parse a money string/number to integer paise. Throws on garbage. */
export function toPaise(v) {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n))
        throw new Error(`invalid money value: ${v}`);
    return Math.round(n * 100);
}
/** Integer paise → Money string with 2 decimals. */
export function fromPaise(paise) {
    const sign = paise < 0 ? '-' : '';
    const abs = Math.abs(Math.round(paise));
    const rupees = Math.floor(abs / 100);
    const p = abs % 100;
    return `${sign}${rupees}.${String(p).padStart(2, '0')}`;
}
/** Round a rupee number to 2 dp (half-up), returning Money. */
export function money(v) {
    return fromPaise(toPaise(v));
}
export function addMoney(...vals) {
    return fromPaise(vals.reduce((s, v) => s + toPaise(v), 0));
}
export function subMoney(a, b) {
    return fromPaise(toPaise(a) - toPaise(b));
}
/** Numeric value of a money string (for comparisons/sorts only). */
export function moneyNum(v) {
    return toPaise(v) / 100;
}
/**
 * Format for display with Indian digit grouping (₹12,34,567.00).
 * Symbol optional so it works in table cells and exports.
 */
export function formatINR(v, opts = {}) {
    // Display helper — tolerate a missing/garbage value with an em-dash rather
    // than throwing (a thrown formatINR crash-rendered whole pages when an API
    // omitted a money field). Money MATH still uses the strict toPaise.
    if (v == null || v === '' || !Number.isFinite(typeof v === 'number' ? v : Number(v)))
        return '—';
    const paise = toPaise(v);
    const neg = paise < 0;
    const abs = Math.abs(paise);
    const rupees = Math.floor(abs / 100);
    const p = String(abs % 100).padStart(2, '0');
    const s = String(rupees);
    // Indian grouping: last 3 digits, then groups of 2.
    let grouped;
    if (s.length <= 3) {
        grouped = s;
    }
    else {
        const last3 = s.slice(-3);
        const rest = s.slice(0, -3);
        grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }
    const sym = opts.symbol === false ? '' : '₹';
    // No trailing ".00" (owner 2026-08-16: "there should be no decimal"). Payouts
    // are now computed in whole rupees, so printing two zeroes on every figure is
    // noise the owner asked to be rid of.
    //
    // Paise are still shown WHEN THERE ARE ANY. Batches paid before the change
    // legitimately carry them and match the bank statement — blanket-stripping
    // would silently misreport ₹56,78,842.03 as ₹56,78,842, which is a different
    // number from the one that left the account.
    const showPaise = abs % 100 !== 0;
    return `${neg ? '-' : ''}${sym}${grouped}${showPaise ? `.${p}` : ''}`;
}
export function payoutRupees(r) {
    const R = (v) => Math.round(Number(v) || 0);
    const gross = R(r.gross_amount);
    const tds = R(r.tds_amount);
    const net = gross - tds;
    // Prefer the explicit pair; fall back to the signed adjustment column.
    const hasPair = r.addition_amount !== undefined || r.deduction_amount !== undefined;
    const signed = R(r.adjustment_amount);
    const addition = hasPair ? R(r.addition_amount) : Math.max(0, signed);
    const deduction = hasPair ? R(r.deduction_amount) : Math.max(0, -signed);
    return { gross, tds, net, addition, deduction, total: net + addition - deduction };
}
//# sourceMappingURL=money.js.map